import React, { useMemo, useState, useRef, useEffect } from "react";
import { useDropzone, FileRejection, Accept, DropEvent } from "react-dropzone";
import { useMutation, useQuery, gql } from "@apollo/client";
import classNames from "classnames";
import { DndProvider, useDrag, useDrop, DropTargetMonitor } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Button from "components/base/Button";
import imageIcon from "assets/img/icons/image-icon.png";
import CustomImageAttachmentPreview from "components/custom/CustomImageAttachmentPreview";

// ---------- (A) GraphQL mutation ----------
export const GET_PRODUCT_IMAGES = gql`
  query sellerProductsDetails($productId: Int!) {
    sellerProductDetails(product_id: $productId) {
      images {
        id
        url
        label
        roles
      }
    }
  }
`;

export const UPLOAD_FILES = gql`
  mutation UploadFiles($input: [UploadInput!]!) {
    UploadFiles(input: $input) {
      items {
        name
        full_path
      }
    }
  }
`;

// ---------- (B) Types ----------
interface FileAttachment {
  name: string;
  preview: string;               // URL or local preview
  originalUrl?: string;          // API URL (must be added)
  size: number;                  // e.g. in KB
  resolution: string;            // 'width x height px'
  uploadProgress?: number;
  isUploading?: boolean;
  roles?: Record<string, boolean>; // keys are Magento enum values
}

interface CustomDropzoneProps {
  className?: string;
  size?: "sm";
  reactDropZoneProps?: any;
  accept?: Accept;
  noPreview?: boolean;
  onDrop?: (newPreviews: FileAttachment[]) => void;
  defaultFiles?: FileAttachment[];
  onFileRemove?: (fileName: string, index?: number) => void;
  onFileUpdate?: (index: number, updatedData: Partial<FileAttachment>) => void;
  onFileIndexChange?: (fromIndex: number, toIndex: number) => void;
  productId: number;
  children?: React.ReactNode;
}

// For dragging items
const ItemType = "IMAGE";
interface DragItem {
  index: number;
  id: string;
  type: string;
}

// ---------- (C) DraggableImage component ----------
const DraggableImage: React.FC<{
  id: string;
  index: number;
  moveImage: (dragIndex: number, hoverIndex: number) => void;
  children: React.ReactNode;
}> = ({ id, index, moveImage, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<DragItem>({
    accept: ItemType,
    hover(item, monitor: DropTargetMonitor) {
      if (!ref.current || item.index === index) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const threshold = 20;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      const isWithinYThreshold =
        hoverClientY < threshold ||
        hoverClientY > hoverBoundingRect.height - threshold;
      const isWithinXThreshold =
        hoverClientX < threshold ||
        hoverClientX > hoverBoundingRect.width - threshold;

      if (isWithinYThreshold || isWithinXThreshold) {
        setTimeout(() => {
          moveImage(item.index, index);
          item.index = index;
        }, 100);
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id, index, type: ItemType },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>
      {children}
    </div>
  );
};

// ---------- (D) Main Dropzone component ----------
export default function CustomDropzone({
  className,
  size,
  onDrop,
  accept,
  noPreview,
  reactDropZoneProps,
  onFileRemove,
  onFileUpdate,
  onFileIndexChange,
  children,
  defaultFiles = [],
  productId,
}: CustomDropzoneProps) {
  const [previews, setPreviews] = useState<FileAttachment[]>(defaultFiles);
  const [imageRoles, setImageRoles] = useState<{ [role: string]: number }>({});
  const baseUrl = process.env.REACT_APP_MEDIA_SERVER_API_URL;

  // Maps friendly labels to Magento enum values
  const ROLE_MAP: Record<string, string> = {
    Base:      "image",
    Small:     "small_image",
    Thumbnail: "thumbnail",
    OG_Image:  "magefan_og_image",
    Swatch:    "swatch_image",
  };

  // Apollo hooks
  const [uploadFilesMutation] = useMutation(UPLOAD_FILES);
  const { data } = useQuery(GET_PRODUCT_IMAGES, { variables: { productId } });

  // Fetch existing images
  useEffect(() => {
    if (data?.sellerProductDetails?.images) {
      const fetched: FileAttachment[] = data.sellerProductDetails.images.map(
        (img: { id: number; url: string; label: string; roles?: string[] }) => {
          // build roles map with Magento keys
          const roles: Record<string,boolean> = {};
          Object.values(ROLE_MAP).forEach(mag => {
            roles[mag] = img.roles?.includes(mag) || false;
          });
          return {
            name: img.label || "Product Image",
            preview: img.url,
            originalUrl: img.url,
            size: 0,
            resolution: "Unknown",
            uploadProgress: 100,
            isUploading: false,
            roles,
          };
        }
      );
      setPreviews(fetched);
      initializeRoles(fetched);
    }
  }, [data]);

  // Initialize roles whenever previews change
  useEffect(() => {
    initializeRoles(previews);
  }, []);

  // ----------- (1) Roles handling -----------
  function initializeRoles(files: FileAttachment[]) {
    // map magRole -> index
    const initial: Record<string, number> = {};
    files.forEach((file, i) => {
      if (file.roles) {
        Object.entries(file.roles).forEach(([mag, has]) => {
          if (has) initial[mag] = i;
        });
      }
    });
    // if none set, assign all to first
    if (!Object.keys(initial).length && files.length) {
      Object.values(ROLE_MAP).forEach(mag => initial[mag] = 0);
    }
    setImageRoles(initial);
    // update previews roles entirely
    setPreviews(prev =>
      prev.map((f,i) => {
        const roles: Record<string,boolean> = {};
        Object.values(ROLE_MAP).forEach(mag => {
          roles[mag] = initial[mag] === i;
        });
        return { ...f, roles };
      })
    );
  }

  // ----------- (2) Remove file -----------
  function handleRemoveFile(index: number) {
    setPreviews(prev => {
      const upd = prev.filter((_,i) => i!==index);
      // reassign roles to 0 if needed
      const removed = prev[index];
      if (removed?.roles && upd.length) {
        Object.entries(removed.roles).forEach(([mag,has]) => {
          if (has) upd[0].roles![mag] = true;
        });
      }
      // update imageRoles
      setImageRoles(ir => {
        const nr: Record<string,number> = {};
        Object.entries(ir).forEach(([mag,i]) => {
          if (i===index) {
            if (upd.length) nr[mag]=0;
          } else if (i>index) {
            nr[mag]=i-1;
          } else nr[mag]=i;
        });
        return nr;
      });
      return upd;
    });
    onFileRemove?.(previews[index].name, index);
  }

  // ----------- (3) Drag & drop reorder -----------
  function moveImage(fromIndex: number, toIndex: number) {
    setPreviews(prev => {
      const arr = [...prev];
      const [m] = arr.splice(fromIndex,1);
      arr.splice(toIndex,0,m);
      // shift imageRoles
      setImageRoles(ir => {
        const nr: Record<string,number> = {};
        Object.entries(ir).forEach(([mag,i]) => {
          if (i===fromIndex) nr[mag]=toIndex;
          else if (fromIndex<toIndex && i>fromIndex && i<=toIndex) nr[mag]=i-1;
          else if (fromIndex>toIndex && i>=toIndex && i<fromIndex) nr[mag]=i+1;
          else nr[mag]=i;
        });
        return nr;
      });
      initializeRoles(arr);
      onFileIndexChange?.(fromIndex,toIndex);
      return arr;
    });
  }

  // ----------- (4) Changing roles -----------
  function handleRoleChange(
    newFriendly: string,
    oldFriendly: string,
    idx: number
  ) {
    const newMag = ROLE_MAP[newFriendly];
    const oldMag = ROLE_MAP[oldFriendly];
    setImageRoles(ir => {
      const nr = { ...ir }; if (oldMag) delete nr[oldMag]; if (newMag) nr[newMag]=idx;
      // update previews
      setPreviews(prev => prev.map((f,i) => {
        const roles: Record<string,boolean> = {};
        Object.values(ROLE_MAP).forEach(mag => roles[mag]= nr[mag]===i);
        return { ...f, roles };
      }));
      // bubble up
      const payloadRoles = Object.fromEntries(
        Object.entries(nr).map(([mag,i]) => [mag, i===idx])
      );
      onFileUpdate?.(idx, { roles: payloadRoles });
      return nr;
    });
  }

  // ----------- (5) base64 + upload  -----------
  async function fileToBase64(file: File): Promise<string> {
    return new Promise((res,rej) => {
      const r = new FileReader();
      r.onload = () => res((r.result as string).split(",")[1]);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
  }
  async function calculateImageResolution(file: File) {
    return new Promise<string>(resolve => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => resolve(`${img.width}x${img.height} px`);
    });
  }

  // ----------- (6) Dropzone -----------
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (acceptedFiles, fileRejections, event) => {
      // placeholders
      const placeholders = acceptedFiles.map((f,i) => {
        const roles: Record<string,boolean> = {};
        Object.values(ROLE_MAP).forEach(mag => roles[mag]= (i===0 && previews.length===0));
        return {
          name: f.name, preview: "", size: Math.round(f.size/1024), resolution: "",
          uploadProgress:0, isUploading:true, roles
        };
      });
      setPreviews(prev => [...prev, ...placeholders]);

      // upload each
      const results: FileAttachment[] = [];
      for (let i=0;i<acceptedFiles.length;i++){
        const file = acceptedFiles[i];
        try{
          const b64 = await fileToBase64(file);
          const resolution = await calculateImageResolution(file);
          const resp = await uploadFilesMutation({ variables:{ input:[{ name:file.name, base64_encoded_file:b64 }] } });
          const items = resp?.data?.UploadFiles?.items || [];
          const path = items[0]?.full_path||"";
          let previewUrl = path;
          if(path) {
            previewUrl = path.replace(/^\/?var\/www\/html\/pub\/media\//,"");
            previewUrl = `${baseUrl}${previewUrl.startsWith('/')?'':'/'}${previewUrl}`;
          }
          const roles: Record<string,boolean> = {};
          Object.values(ROLE_MAP).forEach(mag => roles[mag] = (i===0 && previews.length===0));
          results.push({
            name:file.name, preview:previewUrl, originalUrl:path, size:Math.round(file.size/1024),
            resolution, uploadProgress:100, isUploading:false, roles
          });
        } catch(e) {
          console.error("File upload failed:",e);
        }
      }
      // replace placeholders
      setPreviews(prev => {
        const keep = prev.slice(0, prev.length - placeholders.length);
        return [...keep, ...results];
      });
      initializeRoles([...previews, ...results]);
      onDrop?.(results);
    },
    accept,
    ...reactDropZoneProps
  });

  const imageOnly = useMemo(() => Boolean(accept?.["image/*"]), [accept]);

  // ----------- (E) Render ----------
  return (
    <DndProvider backend={HTML5Backend}>
      {imageOnly && !noPreview && previews.length>0 && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {previews.map((file,i)=>(
            <DraggableImage key={`${file.name}-${i}`} id={file.name} index={i} moveImage={moveImage}>
              <CustomImageAttachmentPreview
                image={file.preview}
                handleClose={()=>handleRemoveFile(i)}
                isFirst={i===0}
                // pass magento enum keys for selected roles
                roles={Object.keys(imageRoles).filter(r=>imageRoles[r]===i)}
                onRoleChange={(newR, oldR)=>handleRoleChange(newR, oldR, i)}
                onFileUpdate={(upd)=>{
                  setPreviews(prev=>{
                    const c=[...prev]; c[i]={...c[i],...upd}; return c;
                  });
                  onFileUpdate?.(i, upd);
                }}
                fileData={file}
                index={i}
              />
            </DraggableImage>
          ))}
        </div>
      )}

      <div {...getRootProps()} className={classNames(className,"dropzone",{ "dropzone-sm": size==="sm" })}>
        <input {...getInputProps()} />
        {children ? children : (
          <div className="text-body-tertiary text-opacity-85 fw-bold fs-9">
            Drag your {imageOnly?"photo":"files"} here <span className="text-body-secondary">or </span>
            <Button variant="link" className="p-0">Browse from device</Button><br />
            <img src={imageIcon} width={size==="sm"?24:40} alt="" className="mt-3" />
          </div>
        )}
      </div>
    </DndProvider>
  );
}
